// Scroll-condense for the merged mobile "one deck" header (2026-08-02, per
// Nika "make navbars on mobile smarter … native-like ux and performance").
// A capture-phase 'scroll' listener on `document` sees every scrollport in
// the page — a plain bubble-phase listener can't, because 'scroll' does not
// bubble — so this is the ONE place that needs to know about scrolling; no
// page wires its own listener (mirrors the edgeSwipe.js one-shot-arm idiom,
// but armed ONCE for the whole document rather than once per page root: the
// deck it drives is a single shell-mounted element, not a per-app rail).
//
// Perf contract: rAF-throttled, passive, and writes NOTHING to signals/state
// — only a `data-nav-condensed` attribute on <html> that the deck's own CSS
// (packages/workspace/app.js injected shell stylesheet) reacts to with a
// transform. Same `html[data-X]` attribute + CSS-reacts idiom as
// data-chat-drawer.
//
// prefers-reduced-motion needs no special-casing here: app.js's global
// reduced-motion floor (`*,*::before,*::after{transition-duration:.01ms
// !important}`) already collapses the deck's transform transition to an
// instant swap — a second override here would be redundant.
import { isCoarsePointer } from './pointerCapability.js'

// FA204 / frank trap — `_armed`/`_scheduled`/`_pendingTarget`/`_lastY` are
// genuine cross-call state (no el/root argument to hang them on; the whole
// point is ONE shared listener set + scroll baseline per scrollport, across
// every independent AppHeader mount that calls this) — re-homed onto
// `window.__navCondenseState` per the pointerCapability.js template. Every
// other helper here was pure/stateless with exactly one caller (this file's
// one export) — inlined alongside it.
//
// `mobileQuery` is the phone-width media query, passed IN by the sole caller
// (`AppHeader.onCreate` → `el.call('armNavCondense', el.scope.MOBILE_HEADER_SEARCH_QUERY)`)
// rather than imported here. `import media from '@symbo-ls/brand/designSystem/media.js'`
// used to live at the top of this file, and `media.mobileL` was read from
// inside this serialized function — a DEFAULT import, which esbuild turns into
// a namespace object (`import_media2.default.mobileL`) that frank cannot lift,
// so the read emitted as `__scope.import_media2.default.mobileL` with nothing
// backing it: `gateActive()` threw a TypeError on the very first scroll event
// at any frank-served runtime (`frank to-json`:
// `scope-read-name-collision`/`import_media2`). AppHeader already keeps the
// token on its own `scope:` (`MOBILE_HEADER_SEARCH_QUERY: media.mobileL`),
// where it bakes to a literal string at serialization time — so the design
// system stays the single source of the value and this file needs no
// cross-package import at all. A caller that passes nothing gets no
// width gate and therefore never condenses: the deck stays visible, which is
// the safe direction (a stuck-hidden header is not).
export const armNavCondense = (mobileQuery) => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  const state = window.__navCondenseState || {
    armed: false,
    scheduled: false,
    pendingTarget: null,
    // Per-scrollport last-known position. Never reactive: a mid-scroll
    // pixel offset has no business on a signal. Keyed by the scroll NODE
    // (not the deck), so a tickets board and a chat list each track their
    // own delta correctly.
    lastY: new WeakMap()
  }
  window.__navCondenseState = state
  if (state.armed) return
  state.armed = true

  const HYSTERESIS_PX = 12
  const ALWAYS_VISIBLE_PX = 24

  const gateActive = () =>
    typeof window !== 'undefined' &&
    isCoarsePointer() &&
    !!mobileQuery &&
    !!window.matchMedia?.(mobileQuery)?.matches

  // 'scroll' fires with `document`/`window` as the target for the root
  // scroller — normalize both to documentElement, the node whose scrollTop
  // is actually meaningful.
  const resolveNode = (target) =>
    target === document || target === window ? document.documentElement : target

  const resolveY = (target) => {
    if (target === document || target === window) {
      return window.scrollY || document.documentElement.scrollTop || 0
    }
    return target && typeof target.scrollTop === 'number' ? target.scrollTop : null
  }

  const condense = () => {
    const html = document.documentElement
    if (html.dataset.navCondensed === 'true') return
    html.dataset.navCondensed = 'true'
  }

  const reveal = () => {
    const html = document.documentElement
    if (!('navCondensed' in html.dataset)) return
    delete html.dataset.navCondensed
  }

  const processScroll = (target) => {
    if (!gateActive()) {
      // Width/pointer no longer qualifies (e.g. a resize crossed @mobileL)
      // — never leave the deck stuck hidden outside the context that's
      // allowed to hide it.
      reveal()
      return
    }
    const y = resolveY(target)
    if (y == null) return
    const node = resolveNode(target)
    const last = state.lastY.get(node)
    state.lastY.set(node, y)
    // Always visible near the top — a user who scrolls back up to the
    // very start of the content shouldn't have to also out-scroll the
    // hysteresis band to get the deck back.
    if (y < ALWAYS_VISIBLE_PX) {
      reveal()
      return
    }
    // First sample for this scrollport — record the baseline, no
    // direction to act on yet (a fresh scrollport must never condense on
    // its very first tick).
    if (last == null) return
    const dy = y - last
    if (dy > HYSTERESIS_PX) condense()
    else if (dy < -HYSTERESIS_PX) reveal()
  }

  const onScroll = (e) => {
    state.pendingTarget = e.target
    if (state.scheduled) return
    state.scheduled = true
    requestAnimationFrame(() => {
      state.scheduled = false
      processScroll(state.pendingTarget)
    })
  }

  // Focus must never be trapped behind a condensed deck — reveal the
  // instant focus lands on anything inside it (tab-key navigation, a
  // screen reader's virtual cursor, an autofocused field).
  const onFocusIn = (e) => {
    const deck = document.querySelector('[data-key="AppHeader"]')
    if (deck && e.target && deck.contains(e.target)) reveal()
  }

  // A fresh route/sheet is a fresh screen — same expectation as a native
  // app, which always shows its own nav bar on arrival regardless of where
  // the previous screen's scroll position left off. Reuses the same
  // pushState-patch events HeaderTabs.onCreate's syncActive already
  // listens for, so this adds no new instrumentation to the shell.
  const onNav = () => reveal()

  // Width/pointer can change without a scroll (an iPad rotating, a resized
  // browser window) — re-validate the gate so a stale condensed deck can
  // never survive outside the phone+coarse context that's allowed to hide
  // it.
  const onResize = () => {
    if (!gateActive()) reveal()
  }

  // capture:true — 'scroll' does not bubble, so this is the only phase
  // that sees a nested scrollport (a tickets board, a chat list, …) fire
  // at all.
  document.addEventListener('scroll', onScroll, { capture: true, passive: true })
  document.addEventListener('focusin', onFocusIn)
  window.addEventListener('popstate', onNav)
  window.addEventListener('locationchange', onNav)
  window.addEventListener('resize', onResize, { passive: true })
}
